/**
 * Take all rooms in parameters, output base64 svg
 * Output example : https://ideine-my.sharepoint.com/:i:/g/personal/thierry_levisse_ideine_fr/EVPE9m3CMgxHqO9-mDJqRaUBWkUxy3DllRJIa6kG-vmivw?e=gAiDjQ
 * @constructor
 */
import React from 'react';

import { MapContextState } from '../../../../Context/MapContext';
import { ExportBandsInRoom, SvgByRoom } from '../LaizeExportBands';
import { IRoom } from 'Plugins/FloorPlan/Models/IRoom';
import { LaizeCalculatorResult } from 'Plugins/FloorPlan/Components/Laizes/Laize';
import { SvgHelpers } from '../../Helpers/SvgGenerationHelpers';
import { ExportMapImageHelper } from '../../Helpers/ExportMapPngHelper';
import { BandsInRoomContent } from 'Plugins/FloorPlan/Components/Laizes/Dialog/ZoomDialogs/BandsInRoomDialog';

export const useExportMapBandsInRoomStudio = ({ rooms = [], laizeResults = [], busy }: Partial<MapContextState>) => {
    const svgHolders = React.useRef<SvgByRoom[]>([]);
    const [exportItems, setExportItems] = React.useState<ExportBandsInRoom[]>([]);

    React.useEffect(() => {
        svgHolders.current = rooms.map((room) => ({ roomId: room.roomId! }));
        if (busy || !laizeResults.length) {
            setExportItems([]);
        } else {
            setExportItems(
                rooms
                    .filter((room) => laizeResults.some((x) => x.roomIds?.some((y) => y === room.roomId)))
                    .map((room) =>
                        Preview({
                            room,
                            laizeResult: laizeResults.find((x) => x.roomIds?.some((y) => y === room.roomId))
                                ?.calculator,
                            getHolder: () => svgHolders.current.find((r) => r.roomId === room.roomId)!,
                        })
                    )
            );
        }
    }, [busy, rooms.length, laizeResults.length]);

    return { exportItems };
};

type ExportBandsInRoomParams = {
    room: IRoom;
    laizeResult?: LaizeCalculatorResult;
    getHolder: () => SvgByRoom;
};

export const Preview = (props: ExportBandsInRoomParams): ExportBandsInRoom => {
    const { room, laizeResult, getHolder } = props;
    const captureSvg = (toBase64: boolean = true) => {
        const holder = getHolder();
        return SvgHelpers.capture(holder?.svgWrapper?.current?.outerHTML, toBase64);
    };

    const capturePng = (toBase64: boolean = true) => {
        const imageSrc = captureSvg(toBase64);
        return ExportMapImageHelper.exportMapPng(imageSrc);
    };

    return {
        room,
        captureSvg,
        capturePng,
        getHolder,
        renderView: ({ id }: React.HTMLAttributes<any> = {}) => (
            <BandsInRoomContent
                key={id}
                setSvgWrapper={(ref) => {
                    const holder = getHolder();
                    if (holder) {
                        holder.svgWrapper = ref;
                    }
                }}
                room={room}
                laizeResult={laizeResult!}
                exportMode
            />
        ),
    };
};
